import type { TierIIBackendTypes } from '../../../tri-backend/tierii/tierii-backend';

export type TierIIWorkflowStatus = TierIIBackendTypes['tierIISubmissionStatusGet']['response'];

export const TIERII_WORKFLOW_STATUS_DEFAULT: TierIIWorkflowStatus = {
  hasProducts: false,
  isDoneInventoryQuestionnaire: false,
  isDoneReviewingThresholds: false,
  isDoneReportForm: false,
  submissionStatus: 'not-started',
  submittedAt: null,
};

export type TierIIWorkflowRoute =
  | 'Facility Products'
  | 'TierII Inventory Questionnaire'
  | 'TierII Inventory Threshold'
  | 'TierII Report Form'
  | 'TierII Export';

export interface TierIIWorkflowStep {
  route: TierIIWorkflowRoute;
  title: string;
  description: string;
  icon: string;
  isDone: boolean;
}

/**
 * The Tier II workflow, shared by every screen that shows progress. As in the TRI workflow, a
 * review stage is done only once it has been explicitly marked complete; the backend clears that
 * flag whenever the stage's data stops passing its checks.
 */
export function tierIIWorkflowSteps(status: TierIIWorkflowStatus): TierIIWorkflowStep[] {
  return [
    {
      route: 'Facility Products',
      title: 'Add Products',
      description: 'Add the hazardous chemicals and products stored at this facility.',
      icon: 'fa-boxes-stacked',
      isDone: status.hasProducts,
    },
    {
      route: 'TierII Inventory Questionnaire',
      title: 'Inventory Questionnaire',
      description: 'Answer questions about how each product is stored and used.',
      icon: 'fa-clipboard-list',
      isDone: status.isDoneInventoryQuestionnaire,
    },
    {
      route: 'TierII Inventory Threshold',
      title: 'Review Thresholds',
      description: 'Confirm which chemicals exceed EPCRA reporting thresholds.',
      icon: 'fa-scale-balanced',
      isDone: status.isDoneReviewingThresholds,
    },
    {
      route: 'TierII Report Form',
      title: 'Report Form',
      description: 'Complete hazard, storage, and quantity details for each item.',
      icon: 'fa-file-lines',
      isDone: status.isDoneReportForm,
    },
    {
      route: 'TierII Export',
      title: 'Submit Report',
      description: 'Hand your completed Tier II report off for filing.',
      icon: 'fa-paper-plane',
      isDone: status.submissionStatus !== 'not-started',
    },
  ];
}

export function tierIIWorkflowPercentage(steps: TierIIWorkflowStep[]): number {
  if (steps.length === 0) return 0;
  return Math.round((steps.filter((step) => step.isDone).length / steps.length) * 100);
}
